import { computed } from '@ember/object';
import { alias, notEmpty, or, gt } from '@ember/object/computed';
import ISO8601 from '@square/dashboard-data/models/squareup/bills/iso8601-date';
import ThirdPartyFeeAmountDetail from '@square/dashboard-data/models/squareup/beemo/api/v2/models/third-party-fee-amount-detail';
import { FeePlan } from '@square/dashboard-data/models/squareup/beemo/api/v2/models/square-processing-fee-amount-details';
import Money from '@square/dashboard-data/models/squareup/common/money-proto';
import { ProtoBaseModel, attr, repeated } from '@square/dashboard-data/utils/ember-model-stubs';
import Enum from '@square/dashboard-utilities/utils/enum';
const FeeType = new Enum(['FIXED_RATE', 'COST_PLUS']);

// Note: The adjustment fee amount is a ReportingAmounts proto that is slightly different
// from the tender's ProcessingFeeAmounts.
// The date is an ISO8601 date instead of a DateTime. This is why we need
// to create a mirror image here to serialize properly, instead of reusing the tenders.
const AdjustmentFee = ProtoBaseModel.extend({
  money: attr(Money),
  adjustedAt: attr(ISO8601)
});
const SquareProcessingFeeAmountDetails = ProtoBaseModel.extend({
  /** Initial fee amount we charged. This number is always 0 or negative. */
  initialFeeMoney: attr(Money),
  taxOnFeeMoney: attr(Money),
  adjustmentFeeMoney: attr(Money),
  adjustmentFee: repeated(AdjustmentFee),
  adjustmentFees: alias('adjustmentFee'),
  feeType: attr(FeeType),
  feePlan: attr(FeePlan),
  totalFeeMoney: computed('initialFeeMoney', 'taxOnFeeMoney', function () {
    const total = this.initialFeeMoney;
    if (this.taxOnFeeMoney) {
      return total.add(this.taxOnFeeMoney);
    }
    return total;
  }),
  freeProcessingFeeMoney: attr(Money)
});
export const ProcessingFeeAmounts = ProtoBaseModel.extend({
  /** The processing fees that goes to Square. */
  squareProcessingFeeMoney: attr(Money),
  /** The tax on square_processing_fee_money. */
  taxOnProcessingFeeMoney: attr(Money),
  /** Breakdown of the processing fee, deprecated in favor of squareProcessingFeeAmountDetailList */
  squareProcessingFeeAmountDetails: attr(SquareProcessingFeeAmountDetails),
  squareProcessingFeeAmountDetailList: repeated(SquareProcessingFeeAmountDetails),
  thirdPartyFeeAmountDetails: repeated(ThirdPartyFeeAmountDetail),
  squareProcessingFeeWithTax: computed('squareProcessingFeeMoney', 'taxOnProcessingFeeMoney', function () {
    const total = this.squareProcessingFeeMoney;
    if (this.taxOnProcessingFeeMoney) {
      return total.add(this.taxOnProcessingFeeMoney);
    }
    return total;
  }),
  hasThirdPartyFeeAmountDetails: notEmpty('thirdPartyFeeAmountDetails'),
  hasAdditionalFeeDetails: or('hasThirdPartyFeeAmountDetails', 'squareProcessingFeeAmountDetails.isCostPlus'),
  // TODO: remove these when squareProcessingFeeAmountDetails fully deprecated in favor of
  // squareProcessingFeeAmountDetailList. Note these are also used in Beemo's v2/models/processing-fee-amounts
  hasSquareProcessingFeeAmountDetailList: gt('squareProcessingFeeAmountDetailList.length', 0),
  feeAmountDetails: computed('hasSquareProcessingFeeAmountDetailList', 'squareProcessingFeeAmountDetails', 'squareProcessingFeeAmountDetailList', function () {
    return this.hasSquareProcessingFeeAmountDetailList ? this.squareProcessingFeeAmountDetailList : [this.squareProcessingFeeAmountDetails];
  })
});
export default ProtoBaseModel.extend({
  processingFeeMoney: attr(Money),
  processingFeeAmounts: attr(ProcessingFeeAmounts)
});